import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MapIcon from '@mui/icons-material/Map';
import GroupsIcon from '@mui/icons-material/Groups';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { SEOHead, SEO_PAGES } from '../seo/SEOHead';
import { InstallExtensionButton } from '../common/InstallExtensionButton';
import { useAuth } from '../../contexts/AuthContext';
import { useCredits } from '../../hooks/useCredits';

const creditPacks = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 50,
    price: 1.99,
    pricePerCredit: '0.040',
    badge: null,
  },
  {
    id: 'standard',
    name: 'Standard',
    credits: 150,
    price: 4.99,
    pricePerCredit: '0.033',
    badge: 'Popular',
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 400,
    price: 11.99,
    pricePerCredit: '0.030',
    badge: null,
  },
  {
    id: 'power',
    name: 'Power',
    credits: 1000,
    price: 24.99,
    pricePerCredit: '0.025',
    badge: 'Best Value',
  },
];

const freeFeatures = [
  { icon: <SearchIcon />, text: 'Unlimited SAM.gov browsing' },
  { icon: <TrendingUpIcon />, text: 'Instant bid estimates' },
  { icon: <AnalyticsIcon />, text: 'Historical award trends' },
  { icon: <GroupsIcon />, text: 'Competitor & incumbent lookup' },
  { icon: <MapIcon />, text: 'Geographic distribution maps' },
  { icon: <DescriptionIcon />, text: '5 free credits to start' },
];

const creditUsage = [
  { action: 'Quick Document Summary', credits: '2-20', description: 'Based on file size' },
  { action: 'Full Document Analysis', credits: '4-40', description: '2x Quick Summary rate' },
  { action: 'Document Chat Question', credits: '1', description: 'Per question asked' },
  { action: 'AI Email Generation', credits: '2-20', description: 'Based on attachment size' },
];

const fileSizeTiers = [
  { size: 'Small (< 500KB)', pages: '1-10 pages', credits: 2 },
  { size: 'Medium (500KB - 1.25MB)', pages: '11-25 pages', credits: 5 },
  { size: 'Large (1.25MB - 2.5MB)', pages: '26-50 pages', credits: 10 },
  { size: 'Very Large (2.5MB - 5MB)', pages: '51-100 pages', credits: 15 },
  { size: 'Huge (5MB+)', pages: '100+ pages', credits: 20 },
];

const faqs = [
  {
    question: 'Do credits expire?',
    answer: 'No, your purchased credits never expire. Use them whenever you need them.',
  },
  {
    question: 'What happens when I run out of credits?',
    answer: 'You can purchase more credits anytime. All free features continue to work without credits.',
  },
  {
    question: 'Can I get a refund on unused credits?',
    answer: 'Credits are non-refundable, but they never expire so you can use them at your own pace.',
  },
  {
    question: 'How do I know how many credits an analysis will cost?',
    answer: 'Before any analysis, we show you the exact credit cost based on the document size. You can cancel if you prefer not to proceed.',
  },
];

export const SubscriptionDetailsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { balance, loading: creditsLoading, error: creditsError, purchaseCredits, purchasing } = useCredits();
  const [purchasingPack, setPurchasingPack] = useState<string | null>(null);

  const handlePurchase = async (packId: string) => {
    setPurchasingPack(packId);
    await purchaseCredits(packId);
    setPurchasingPack(null);
  };

  return (
    <Box sx={{ py: 6 }}>
      <SEOHead {...SEO_PAGES.plans} />

      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, mb: 6 }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Free to Start, Pay as You Grow
          </Typography>
          <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
            Get 5 free credits instantly. Buy more only when you need them.
          </Typography>
          <InstallExtensionButton location="pricing_hero" color="secondary" />
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* What's Free Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
            Everything Free, Forever
          </Typography>
          <Grid container spacing={3}>
            {freeFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ color: 'success.main' }}>{feature.icon}</Box>
                    <Typography variant="body1" fontWeight={500}>
                      {feature.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Credit Packs Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
            Credit Packs
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2 }}>
            Purchase credits for AI-powered document analysis. Buy once, use anytime.
          </Typography>

          {currentUser && (
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              {creditsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Chip
                  label={`Your Balance: ${balance.available} credits`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 2 }}
                />
              )}
            </Box>
          )}

          {creditsError && (
            <Alert severity="error" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
              {creditsError}
            </Alert>
          )}

          <Grid container spacing={3}>
            {creditPacks.map((pack) => (
              <Grid item xs={12} sm={6} md={3} key={pack.id}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: pack.badge ? '2px solid' : '1px solid',
                    borderColor: pack.badge === 'Best Value' ? 'success.main' : pack.badge ? 'primary.main' : 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {pack.badge && (
                    <Chip
                      label={pack.badge}
                      color={pack.badge === 'Best Value' ? 'success' : 'primary'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    />
                  )}
                  <CardContent sx={{ textAlign: 'center', pt: pack.badge ? 4 : 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      {pack.name}
                    </Typography>
                    <Typography variant="h3" color="primary" fontWeight={700}>
                      ${pack.price}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {pack.credits} credits
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      ${pack.pricePerCredit} per credit
                    </Typography>
                    <Box sx={{ mt: 'auto' }}>
                      {currentUser ? (
                        <Button
                          variant="contained"
                          color={pack.badge === 'Best Value' ? 'success' : 'primary'}
                          fullWidth
                          onClick={() => handlePurchase(pack.id)}
                          disabled={purchasing}
                          startIcon={purchasingPack === pack.id ? <CircularProgress size={20} color="inherit" /> : <ShoppingCartIcon />}
                        >
                          {purchasingPack === pack.id ? 'Processing...' : 'Buy Now'}
                        </Button>
                      ) : (
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          href="/login?redirect=/plans"
                        >
                          Login to Purchase
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          {!currentUser && (
            <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
              <a href="/login?redirect=/plans" style={{ color: 'inherit' }}>Log in</a> to purchase credits, or install the Chrome extension to get started with 5 free credits.
            </Typography>
          )}
        </Box>

        {/* What Uses Credits Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
            What Uses Credits?
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><strong>Action</strong></TableCell>
                      <TableCell align="center"><strong>Credits</strong></TableCell>
                      <TableCell><strong>Notes</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {creditUsage.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.action}</TableCell>
                        <TableCell align="center">
                          <Chip label={row.credits} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {row.description}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon color="primary" />
                  Credit Cost by File Size
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Document analysis credits are based on estimated page count:
                </Typography>
                <List dense>
                  {fileSizeTiers.map((tier, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={tier.size}
                        secondary={`${tier.pages} = ${tier.credits} credits`}
                      />
                    </ListItem>
                  ))}
                </List>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                  Full Analysis doubles the credit cost shown above.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
            Frequently Asked Questions
          </Typography>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Win More Contracts?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Install free and get 5 credits to try AI-powered document analysis.
          </Typography>
          <InstallExtensionButton location="pricing_footer" color="secondary" />
        </Paper>
      </Container>
    </Box>
  );
};
